import { useEffect, useMemo } from 'react';

import { PrescriptionHistoryDailyGroup } from '@dreryk/egabinet-prescription-frontend-api';

import { EmptyStatePlaceholder, Table, TableKeyExtractor } from '@dreryk/react-components';

import { translationKeys } from '../translations';

import {
   usePatientPrescriptionHistoryRowDetailsRenderer,
   usePatientPrescriptionHistoryTableColumns,
   usePatientPrescriptionHistoryTableDataConfiguration,
} from './PatientPrescriptionHistoryTable.hooks';
import { PatientPrescriptionHistoryTableDataAccessor } from './PatientPrescriptionHistoryTableDataAccessor';
import {
   PrescriptionHistoryFilters,
   PrescriptionHistoryRenderAccordionRowActions,
   PrescriptionHistoryRenderOptionsCell,
   PrescriptionHistorySelection,
} from './PatientPrescriptionHistoryTable.types';

const PAGE_SIZE = 10;
const NUMBER_OF_PAGES_TO_BUFFER = 3;

export const keyExtractor: TableKeyExtractor<PrescriptionHistoryDailyGroup> = (bean) => String(bean.issuedOn);

export type PatientPrescriptionHistoryTableProps = {
   readonly patientId: number;
   readonly filters: PrescriptionHistoryFilters;
   readonly renderOptionsCell?: PrescriptionHistoryRenderOptionsCell;
   readonly renderAccordionRowActions?: PrescriptionHistoryRenderAccordionRowActions;
   readonly selection?: PrescriptionHistorySelection;
};

export const PatientPrescriptionHistoryTable = (props: PatientPrescriptionHistoryTableProps): JSX.Element => {
   const dataConfiguration = usePatientPrescriptionHistoryTableDataConfiguration(props.patientId, props.filters);
   const columns = usePatientPrescriptionHistoryTableColumns(props.renderAccordionRowActions, props.selection);
   const rowDetailsRenderer = usePatientPrescriptionHistoryRowDetailsRenderer(props.renderOptionsCell, props.selection);
   const data = dataConfiguration.dataPart?.data;
   const reconcileSelection = props.selection?.reconcileSelection;

   useEffect(() => {
      if (data) {
         reconcileSelection?.(data);
      }
   }, [data, reconcileSelection]);

   const defaultRowDetailsOpen = useMemo(() => {
      const data = dataConfiguration.dataPart?.data ?? [];
      return data.reduce<Record<string, boolean>>((acc, item) => {
         acc[String(item.issuedOn)] = true;
         return acc;
      }, {});
   }, [dataConfiguration.dataPart?.data]);

   return (
      <Table<PrescriptionHistoryDailyGroup>
         columns={columns}
         dataConfiguration={dataConfiguration}
         keyExtractor={keyExtractor}
         rowDetailsRenderer={rowDetailsRenderer}
         defaultRowDetailsOpen={defaultRowDetailsOpen}
         dataAccessor={PatientPrescriptionHistoryTableDataAccessor}
         pageSize={PAGE_SIZE}
         numberOfPagesToBuffer={NUMBER_OF_PAGES_TO_BUFFER}
         compact
         placeholder={
            <EmptyStatePlaceholder
               heading={translationKeys.table.noPrescriptionHistory}
               testId={'noPrescriptionHistory'}
            />
         }
         testId={'patientPrescriptionHistory'}
      />
   );
};
